import clsx from "clsx";
import { AlertCircle, AlertTriangle, Info } from "react-feather";

import styles from "./alert.module.css";

export type AlertSeverity = "error" | "warning" | "info";

interface AlertProps {
  "data-testid"?: string;
  "data-test-role"?: string;
  severity?: AlertSeverity;
  /** Short, single-line summary of the alert. */
  title: string;
  /** Optional supporting help text or details rendered below the title. */
  children?: React.ReactNode;
  /**
   * Optional trailing reference rendered right-aligned in the header, e.g. a
   * machine-readable error code or line number.
   */
  reference?: React.ReactNode;
  className?: string;
}

const ICONS: Record<AlertSeverity, typeof AlertCircle> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

/**
 * An inline, severity-aware callout for surfacing errors, warnings, and
 * informational notices within a page (as opposed to AppError, which owns the
 * whole viewport for fatal failures).
 */
function Alert({
  "data-testid": testId = "pallet-alert",
  "data-test-role": testRole = "alert",
  severity = "error",
  title,
  children,
  reference,
  className,
}: AlertProps) {
  const Icon = ICONS[severity];

  return (
    <div
      role="alert"
      data-testid={testId}
      data-test-role={testRole}
      data-severity={severity}
      className={clsx(styles.alert, styles[severity], className)}
    >
      <Icon className={styles.icon} size={18} aria-hidden={true} />
      <div className={styles.body}>
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          {reference ? (
            <span className={styles.reference}>{reference}</span>
          ) : null}
        </div>
        {children ? <div className={styles.help}>{children}</div> : null}
      </div>
    </div>
  );
}

export default Alert;
